/**
 * CancelledBanner — replaces the danger zone once a workshop is cancelled
 * (BON-202). States that all booked customers were MOVED to the target workshop
 * and emailed the new details (transfer framing — never "refunded"). When the
 * banner renders right after the cancel, it also surfaces the notification
 * summary (and a soft warning if any email failed). On a later page load — when
 * only the cancelled lifecycle is known — it shows the standing transfer notice
 * without those one-time details.
 */
import { DANGER_RED } from '../../primitives';

export interface CancelledBannerDetail {
  /** "Type · Venue · Date" of the workshop customers were moved to. */
  targetLabel: string;
  notified: number;
  failed: number;
}

export interface CancelledBannerProps {
  /** One-time details from the just-completed cancel, or null on a later load. */
  detail: CancelledBannerDetail | null;
}

export function CancelledBanner({ detail }: CancelledBannerProps) {
  return (
    <section
      className="nb-card"
      style={{ border: '1px solid rgba(192,57,43,.35)', borderColor: 'rgba(192,57,43,.35)' }}
    >
      <div className="nb-card-hd" style={{ marginBottom: 8 }}>
        <h3 style={{ color: DANGER_RED }}>Workshop cancelled</h3>
      </div>
      <div style={{ fontSize: 12, color: 'var(--nb-ink)', lineHeight: 1.55 }}>
        {detail ? (
          <>
            All booked customers were moved to <strong>{detail.targetLabel}</strong> and emailed the
            new workshop details.
          </>
        ) : (
          <>
            This workshop was cancelled. All booked customers were moved to another workshop and
            emailed the new details.
          </>
        )}
      </div>

      {detail && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11.5, color: 'var(--nb-muted)' }}>
            {detail.notified} {detail.notified === 1 ? 'customer' : 'customers'} notified.
          </div>
          {detail.failed > 0 && (
            <div role="alert" style={{ fontSize: 11.5, color: 'var(--nb-warn)' }}>
              {detail.failed} {detail.failed === 1 ? 'email' : 'emails'} could not be sent — follow
              up with {detail.failed === 1 ? 'that customer' : 'those customers'} directly.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
